import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MarkdownRenderer } from "../components/MarkdownRenderer";
import { useWorkflow } from "../contexts/WorkflowContext";
import { Event } from "../../shared/types";

export const AgentDetailView: React.FC = () => {
  const { workflowId, agentId } = useParams<{ workflowId: string; agentId: string }>();
  const navigate = useNavigate();
  const { workflow, agents, events } = useWorkflow();
  
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);

  const agent = agents.find(a => a.id === agentId);
  const agentEvents = events.filter(e => e.agentId === agentId);

  const handleBack = () => {
    navigate(`/app/workflow/${workflowId}`);
  };

  const handleEventClick = (event: Event) => {
    setSelectedEvent(event);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "running":
        return "text-green-500";
      case "completed":
        return "text-blue-500";
      case "failed":
        return "text-red-500";
      case "idle":
        return "text-gray-500";
      default:
        return "text-gray-400";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (!agent) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-lg mb-4">Agent not found</div>
          <p className="text-gray-300 mb-4">The requested agent could not be found in this workflow.</p>
          <button
            onClick={handleBack}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded"
          >
            Back to Workflow
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-hidden">
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 p-6 border-b border-border-primary">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
                title="Back to workflow"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-white">{agent.type} Agent</h1>
                <p className="text-gray-400 mt-1">Agent ID: {agent.id}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div
                className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(agent.status || "idle")}`}
              >
                {(agent.status || "idle").toUpperCase()}
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Agent Info */}
            <div className="bg-bg-card border border-border-primary rounded-lg p-6">
              <h2 className="text-lg font-semibold text-white mb-4">Agent Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="text-sm text-gray-400">Type:</span>
                  <span className="ml-2 text-white">{agent.type}</span>
                </div>
                <div>
                  <span className="text-sm text-gray-400">Status:</span>
                  <span className={`ml-2 ${getStatusColor(agent.status || "idle")}`}>
                    {agent.status || "idle"}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-400">Created:</span>
                  <span className="ml-2 text-white">{formatDate(agent.createdAt)}</span>
                </div>
                <div>
                  <span className="text-sm text-gray-400">Updated:</span>
                  <span className="ml-2 text-white">{formatDate(agent.updatedAt)}</span>
                </div>
              </div>
              
              {agent.objective && (
                <div className="mt-4">
                  <span className="text-sm text-gray-400">Objective:</span>
                  <div className="mt-1 text-white">
                    <MarkdownRenderer content={agent.objective} compact={false} />
                  </div>
                </div>
              )}
            </div>

            {/* Agent Events */}
            <div className="bg-bg-card border border-border-primary rounded-lg p-6">
              <h2 className="text-lg font-semibold text-white mb-4">
                Events ({agentEvents.length})
              </h2>
              
              {agentEvents.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  No events recorded for this agent yet.
                </div>
              ) : (
                <div className="space-y-3">
                  {agentEvents.map((event, index) => (
                    <div
                      key={event.id || index}
                      onClick={() => handleEventClick(event)}
                      className="p-4 bg-gray-800/50 border border-gray-700 rounded-lg hover:border-primary-500/50 transition-colors cursor-pointer"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-1 rounded text-xs bg-primary-600 text-white">
                            {event.type}
                          </span>
                          <span className="text-sm text-gray-400">
                            {event.timestamp.toFixed(2)}s
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">
                          #{index + 1}
                        </span>
                      </div>
                      
                      <div className="text-sm text-gray-300">
                        <MarkdownRenderer content={event.content} compact={true} />
                      </div>
                      
                      {event.metadata && Object.keys(event.metadata).length > 0 && (
                        <div className="mt-2 text-xs text-gray-500">
                          <details>
                            <summary className="cursor-pointer hover:text-gray-400">
                              Metadata
                            </summary>
                            <pre className="mt-1 p-2 bg-gray-900 rounded text-xs overflow-x-auto">
                              {JSON.stringify(event.metadata, null, 2)}
                            </pre>
                          </details>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Event Details Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-bg-card border border-border-primary rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold text-white">
                Event Details
              </h3>
              <button
                onClick={() => setSelectedEvent(null)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-400">Type:</span>
                  <span className="ml-2 px-2 py-1 rounded text-xs bg-primary-600 text-white">
                    {selectedEvent.type}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400">Time:</span>
                  <span className="ml-2 text-white font-mono">
                    {selectedEvent.timestamp.toFixed(2)}s
                  </span>
                </div>
              </div>

              <div>
                <span className="text-sm text-gray-400">Content:</span>
                <div className="mt-1 p-3 bg-gray-800/50 rounded border border-gray-600">
                  <MarkdownRenderer
                    content={selectedEvent.content}
                    compact={false}
                  />
                </div>
              </div>

              {selectedEvent.metadata && Object.keys(selectedEvent.metadata).length > 0 && (
                <div>
                  <span className="text-sm text-gray-400">Metadata:</span>
                  <pre className="mt-1 p-3 bg-gray-800/50 rounded border border-gray-600 text-xs overflow-x-auto">
                    {JSON.stringify(selectedEvent.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
